import { supabase } from './supabase'
import { setViewingAs, viewingAs, type ViewAs } from './viewAsState'
import { clearAskSession } from './askOneSession'

export { viewingAs, isReadOnlyRefusal, type ViewAs } from './viewAsState'

/**
 * 176. The three doors. Each acts for the signed-in administrator (they never
 * carry the marker — lib/viewAsState.ts) and each ends in a full reload, so
 * no screen keeps data read as the other person, or as yourself, across the
 * switch. A reload is crude and that is the point: nothing to forget to reset.
 */

type Row = { session_id: string; subject_person_id: string; display_name: string; expires_at: string }

function fromRow(r: Row): ViewAs {
  return { sessionId: r.session_id, personId: r.subject_person_id, displayName: r.display_name, expiresAt: r.expires_at }
}

/** Start seeing the platform as this person. people.view_as; refused otherwise. */
export async function startViewAs(personId: string): Promise<void> {
  const { data, error } = await supabase.rpc('start_view_as', { p_person_id: personId })
  if (error) throw error
  const row = (Array.isArray(data) ? data[0] : data) as Row | undefined
  if (!row) throw new Error('The server did not open a View as session.')
  setViewingAs(fromRow(row))
  // 1 Nov: an Ask One conversation from before belongs to before.
  clearAskSession()
  window.location.hash = '#/'
  window.location.reload()
}

/** Back to yourself. Forgets the session here even if the server call fails. */
export async function stopViewAs(): Promise<void> {
  try {
    await supabase.rpc('end_view_as')
  } finally {
    setViewingAs(null)
    clearAskSession()
    window.location.hash = '#/'
    window.location.reload()
  }
}

/**
 * On a cold load: is the session this tab remembers still the caller's open
 * one? If not (ended in another tab, expired, revoked, a different person
 * signed in) forget it before anything is read. Returns what is current.
 */
export async function confirmViewAs(): Promise<ViewAs | null> {
  const mine = viewingAs()
  if (!mine) return null
  const { data, error } = await supabase.rpc('my_view_as')
  const row = (Array.isArray(data) ? data[0] : data) as Row | undefined
  if (error || !row || row.session_id !== mine.sessionId) {
    setViewingAs(null)
    return null
  }
  const v = fromRow(row)
  setViewingAs(v)
  return v
}
